import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { ArrowLeft, Send, Check, CheckCheck, Pencil, Trash2, X } from 'lucide-react';
import { useApp } from '../../context/AppContext';
import { getAirline } from '../../utils/airlines';
import { FriendProfileSheet } from '../profile/FriendProfileSheet';
import { haptic, hapticMedium } from '../../utils/haptics';
import { AvatarImg } from '../shared/Avatar';
import { useKeyboard } from '../../hooks/useKeyboard';
import type { Message, User } from '../../types';

interface ChatViewProps {
  partnerId: string;
  prefill?: string;
  onClose: () => void;
}

function formatMessageDate(dateStr: string): string {
  const date = new Date(dateStr);
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const yesterday = new Date(today.getTime() - 86400000);
  const msgDate = new Date(date.getFullYear(), date.getMonth(), date.getDate());

  if (msgDate.getTime() === today.getTime()) return 'Today';
  if (msgDate.getTime() === yesterday.getTime()) return 'Yesterday';
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

function formatTime(dateStr: string): string {
  return new Date(dateStr).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: false });
}

export function ChatView({ partnerId, prefill, onClose }: ChatViewProps) {
  const { messages, currentUser, usersById, sendMessage, editMessage, deleteMessage, markConversationRead } = useApp();
  const [text, setText] = useState(prefill ?? '');
  const [sending, setSending] = useState(false);
  const [showProfile, setShowProfile] = useState(false);
  const [activeMsg, setActiveMsg] = useState<string | null>(null);
  const [editingMsg, setEditingMsg] = useState<{ id: string; content: string } | null>(null);
  const longPressTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const bottomRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const { keyboardHeight, isVisible: kbVisible } = useKeyboard();

  const partner: User | undefined = usersById.get(partnerId);
  const airline = partner ? getAirline(partner.airlineCode) : null;

  // Filter messages for this conversation
  const conversationMessages = useMemo(
    () =>
      messages.filter(
        (m) =>
          (m.senderId === partnerId && m.receiverId === currentUser.id) ||
          (m.senderId === currentUser.id && m.receiverId === partnerId)
      ),
    [messages, partnerId, currentUser.id]
  );

  // Mark read on mount and when new messages arrive from partner
  useEffect(() => {
    const hasUnread = conversationMessages.some(
      (m) => m.senderId === partnerId && !m.readAt
    );
    if (hasUnread) {
      markConversationRead(partnerId);
    }
  }, [conversationMessages, partnerId, markConversationRead]);

  // Auto-scroll to bottom
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [conversationMessages.length]);

  // Auto-resize textarea
  useEffect(() => {
    const ta = textareaRef.current;
    if (!ta) return;
    ta.style.height = 'auto';
    ta.style.height = Math.min(ta.scrollHeight, 96) + 'px';
  }, [text]);

  // Scroll to bottom when keyboard appears
  useEffect(() => {
    if (kbVisible) {
      requestAnimationFrame(() => {
        bottomRef.current?.scrollIntoView({ block: 'end' });
      });
    }
  }, [kbVisible]);

  const handleSend = async () => {
    const trimmed = text.trim();
    if (!trimmed || sending) return;
    setSending(true);
    setText('');
    try {
      await sendMessage(partnerId, trimmed);
    } finally {
      setSending(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      if (editingMsg) {
        handleEditSave();
      } else {
        handleSend();
      }
    }
  };

  const startLongPress = useCallback((msgId: string) => {
    longPressTimer.current = setTimeout(() => {
      hapticMedium();
      setActiveMsg(msgId);
    }, 500);
  }, []);

  const cancelLongPress = useCallback(() => {
    if (longPressTimer.current) {
      clearTimeout(longPressTimer.current);
      longPressTimer.current = null;
    }
  }, []);

  const handleEdit = (msg: Message) => {
    setActiveMsg(null);
    setEditingMsg({ id: msg.id, content: msg.content });
    setText(msg.content);
    textareaRef.current?.focus();
  };

  const handleEditSave = async () => {
    if (!editingMsg) return;
    const trimmed = text.trim();
    if (!trimmed) return;
    await editMessage(editingMsg.id, trimmed);
    setEditingMsg(null);
    setText('');
  };

  const handleEditCancel = () => {
    setEditingMsg(null);
    setText('');
  };

  const handleDelete = async (msgId: string) => {
    setActiveMsg(null);
    hapticMedium();
    await deleteMessage(msgId);
  };

  // Group messages into clusters by sender + time proximity (show timestamp once per cluster)
  const clusters = useMemo(() => {
    const result: { date: string; groups: { senderId: string; messages: Message[] }[] }[] = [];
    let currentDate = '';
    let currentGroup: { senderId: string; messages: Message[] } | null = null;

    for (const msg of conversationMessages) {
      const d = formatMessageDate(msg.createdAt);

      if (d !== currentDate) {
        currentDate = d;
        currentGroup = { senderId: msg.senderId, messages: [msg] };
        result.push({ date: d, groups: [currentGroup] });
      } else {
        const lastDateEntry = result[result.length - 1];
        // Group consecutive messages from same sender within 2 minutes
        if (
          currentGroup &&
          currentGroup.senderId === msg.senderId &&
          new Date(msg.createdAt).getTime() - new Date(currentGroup.messages[currentGroup.messages.length - 1].createdAt).getTime() < 120000
        ) {
          currentGroup.messages.push(msg);
        } else {
          currentGroup = { senderId: msg.senderId, messages: [msg] };
          lastDateEntry.groups.push(currentGroup);
        }
      }
    }
    return result;
  }, [conversationMessages]);

  const portalTarget = document.getElementById('app-shell') || document.body;

  return createPortal(
    <div
      className="absolute inset-0 z-[100] flex flex-col bg-slate-900"
      style={kbVisible ? { paddingBottom: `${keyboardHeight}px` } : undefined}
    >
      {/* Header */}
      <div className="shrink-0 bg-slate-800 border-b border-slate-700/50" style={{ paddingTop: 'var(--sat)' }}>
        <div className="flex items-center gap-3 px-3 py-2.5">
          <button
            onClick={onClose}
            className="p-1.5 -ml-1 rounded-lg hover:bg-slate-700/50 active:bg-slate-600/50 transition-colors"
          >
            <ArrowLeft size={22} className="text-slate-200" />
          </button>
          {partner && airline && (
            <button
              onClick={() => { haptic(); setShowProfile(true); }}
              className="flex items-center gap-3 min-w-0 active:opacity-70 transition-opacity"
            >
              <div
                className="w-9 h-9 rounded-full flex items-center justify-center text-sm font-bold shrink-0 overflow-hidden"
                style={{ backgroundColor: airline.color, color: airline.textOnColor }}
              >
                <AvatarImg src={partner.avatarUrl} name={partner.name} />
              </div>
              <div className="min-w-0 text-left">
                <div className="text-[15px] font-semibold text-slate-100 truncate">{partner.name}</div>
                <div className="text-xs text-slate-500">@{partner.handle}</div>
              </div>
            </button>
          )}
        </div>
      </div>

      {/* Messages */}
      <div
        className="flex-1 overflow-y-auto px-4 py-3"
        onClick={() => activeMsg && setActiveMsg(null)}
      >
        {conversationMessages.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full text-slate-500">
            <p className="text-sm font-medium">No messages yet</p>
            <p className="text-xs mt-1 text-slate-600">Say hi!</p>
          </div>
        ) : (
        <div className="min-h-full flex flex-col justify-end">
        {clusters.map((dateEntry) => (
          <div key={dateEntry.date}>
            {/* Date separator */}
            <div className="flex items-center justify-center my-4">
              <span className="text-[11px] font-medium text-slate-500 bg-slate-800 px-3 py-1 rounded-full">
                {dateEntry.date}
              </span>
            </div>
            {/* Message clusters */}
            {dateEntry.groups.map((group, gi) => {
              const isMine = group.senderId === currentUser.id;
              const lastMsg = group.messages[group.messages.length - 1];
              return (
                <div key={gi} className={`flex flex-col mb-3 ${isMine ? 'items-end' : 'items-start'}`}>
                  {/* Partner avatar for received clusters */}
                  {!isMine && partner && airline && (
                    <div className="flex items-end gap-2 w-full">
                      <div
                        className="w-6 h-6 rounded-full flex items-center justify-center text-[9px] font-bold shrink-0 mb-5"
                        style={{ backgroundColor: airline.color, color: airline.textOnColor }}
                      >
                        <AvatarImg src={partner.avatarUrl} name={partner.name} className="w-full h-full object-cover rounded-full" />
                      </div>
                      <div className="flex flex-col gap-0.5 max-w-[75%]">
                        {group.messages.map((msg, mi) => (
                          <div
                            key={msg.id}
                            className={`px-3.5 py-2 text-[15px] leading-snug bg-slate-700 text-slate-100 shadow-sm ${
                              group.messages.length === 1
                                ? 'rounded-2xl rounded-bl-md'
                                : mi === 0
                                  ? 'rounded-2xl rounded-bl-md rounded-bl-sm'
                                  : mi === group.messages.length - 1
                                    ? 'rounded-2xl rounded-tl-sm rounded-bl-md'
                                    : 'rounded-2xl rounded-l-sm'
                            }`}
                          >
                            {msg.content}
                            {msg.editedAt && (
                              <span className="text-[10px] text-slate-400 ml-1.5">edited</span>
                            )}
                          </div>
                        ))}
                        <span className="text-[10px] text-slate-500 px-1 mt-0.5">
                          {formatTime(lastMsg.createdAt)}
                        </span>
                      </div>
                    </div>
                  )}
                  {/* Sent cluster */}
                  {isMine && (
                    <div className="flex flex-col items-end gap-0.5 max-w-[75%]">
                      {group.messages.map((msg, mi) => (
                        <div key={msg.id} className="relative">
                          <div
                            onTouchStart={() => startLongPress(msg.id)}
                            onTouchEnd={cancelLongPress}
                            onTouchCancel={cancelLongPress}
                            onContextMenu={(e) => { e.preventDefault(); hapticMedium(); setActiveMsg(msg.id); }}
                            className={`px-3.5 py-2 text-[15px] leading-snug bg-accent text-white shadow-sm select-none ${
                              activeMsg === msg.id ? 'ring-2 ring-white/30' : ''
                            } ${
                              group.messages.length === 1
                                ? 'rounded-2xl rounded-br-md'
                                : mi === 0
                                  ? 'rounded-2xl rounded-br-sm'
                                  : mi === group.messages.length - 1
                                    ? 'rounded-2xl rounded-tr-sm rounded-br-md'
                                    : 'rounded-2xl rounded-r-sm'
                            }`}
                          >
                            {msg.content}
                            {msg.editedAt && (
                              <span className="text-[10px] text-white/50 ml-1.5">edited</span>
                            )}
                          </div>
                          {/* Action menu */}
                          {activeMsg === msg.id && (
                            <div className="absolute right-0 bottom-full mb-1 flex items-center gap-1 bg-slate-800 border border-slate-700 rounded-xl px-1 py-0.5 shadow-lg z-10 animate-fade-in">
                              <button
                                onClick={() => handleEdit(msg)}
                                className="flex items-center gap-1 px-2.5 py-1.5 text-xs text-slate-200 hover:bg-slate-700 rounded-lg transition-colors"
                              >
                                <Pencil size={12} />
                                Edit
                              </button>
                              <button
                                onClick={() => handleDelete(msg.id)}
                                className="flex items-center gap-1 px-2.5 py-1.5 text-xs text-red-400 hover:bg-red-500/10 rounded-lg transition-colors"
                              >
                                <Trash2 size={12} />
                                Delete
                              </button>
                            </div>
                          )}
                        </div>
                      ))}
                      <div className="flex items-center gap-1 px-1 mt-0.5">
                        <span className="text-[10px] text-slate-500">
                          {formatTime(lastMsg.createdAt)}
                        </span>
                        {lastMsg.readAt ? (
                          <CheckCheck size={12} className="text-accent" />
                        ) : (
                          <Check size={12} className="text-slate-500" />
                        )}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        ))}
        <div ref={bottomRef} />
        </div>
        )}
      </div>

      {/* Input bar */}
      <div
        className="shrink-0 bg-slate-800 border-t border-slate-700/50 px-3 py-2.5"
        style={{ paddingBottom: kbVisible ? '8px' : 'calc(var(--sab) + 8px)' }}
      >
        {/* Edit mode indicator */}
        {editingMsg && (
          <div className="flex items-center gap-2 px-1 pb-2 text-xs">
            <Pencil size={12} className="text-accent" />
            <span className="text-accent font-medium">Editing message</span>
            <button onClick={handleEditCancel} className="ml-auto p-0.5 text-slate-500 hover:text-slate-300">
              <X size={14} />
            </button>
          </div>
        )}
        <div className="flex items-end gap-2.5">
          <textarea
            ref={textareaRef}
            value={text}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={editingMsg ? 'Edit message...' : 'Message...'}
            rows={1}
            className="flex-1 bg-slate-700 text-slate-100 placeholder-slate-500 rounded-2xl px-4 py-2.5 text-[15px] resize-none outline-none focus:ring-2 focus:ring-accent/40 max-h-24"
          />
          <button
            onClick={editingMsg ? handleEditSave : handleSend}
            disabled={!text.trim() || sending}
            className="p-2.5 rounded-full bg-accent text-white disabled:opacity-30 active:scale-95 transition-all shrink-0"
          >
            {editingMsg ? <Check size={18} /> : <Send size={18} />}
          </button>
        </div>
      </div>
      {/* Friend profile sheet */}
      {showProfile && (
        <FriendProfileSheet
          friendId={partnerId}
          onClose={() => setShowProfile(false)}
          onMessage={() => setShowProfile(false)}
        />
      )}
    </div>,
    portalTarget,
  );
}
